import React, { useState, useRef, useEffect, useCallback } from 'react';

interface SidebarProps {
  activeComponent: string;
  setActiveComponent: (component: string) => void;
  onLogout: () => void;
}

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const Ico = ({
  d, size = 18, style = {}, fill = false,
}: {
  d: string | string[]; size?: number; style?: React.CSSProperties; fill?: boolean;
}) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoHome        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />;
const IcoBuilding    = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M3 21h18', 'M5 21V7l7-4 7 4v14', 'M9 21v-4h6v4']} />;
const IcoAndroid     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M7 8h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2v-6a2 2 0 012-2z', 'M5 10a2 2 0 01-2-2M19 10a2 2 0 002-2M9 18v2M15 18v2M9 5l1-3M15 5l-1-3']} />;
const IcoCog         = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 15a3 3 0 100-6 3 3 0 000 6z', 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z']} />;
const IcoDatabase    = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 2C8.13 2 5 3.34 5 5v14c0 1.66 3.13 3 7 3s7-1.34 7-3V5c0-1.66-3.13-3-7-3z', 'M19 9c0 1.66-3.13 3-7 3S5 10.66 5 9', 'M19 14c0 1.66-3.13 3-7 3s-7-1.34-7-3']} />;
const IcoTable       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />;
const IcoBook        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M4 19.5A2.5 2.5 0 016.5 17H20', 'M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z']} />;
const IcoCalTime     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z', 'M12 14v3l2 1']} />;
const IcoIdCard      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M2 5a2 2 0 012-2h16a2 2 0 012 2v14a2 2 0 01-2 2H4a2 2 0 01-2-2V5z', 'M8 10a2 2 0 100-4 2 2 0 000 4z', 'M14 8h4M14 12h4M4 20v-1a4 4 0 018 0v1']} />;
const IcoUserPlus    = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2', 'M9 7a4 4 0 100 8 4 4 0 000-8zM19 8v6M16 11h6']} />;
const IcoClipboard   = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2', 'M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2']} />;
const IcoSync        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15" />;
const IcoRupee       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M6 3h12M6 8h12M15 21L6 8M9 8a3 3 0 000 6h2l5 7" />;
const IcoWrench      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />;
const IcoChevronDown = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M6 9l6 6 6-6" />;
const IcoSignOut     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4', 'M16 17l5-5-5-5', 'M21 12H9']} />;
const IcoGrid        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />;
const IcoSidebarOpen  = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M3 12h18', 'M3 6h18', 'M3 18h18']} />;
const IcoSidebarClose = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M19 12H5', 'M12 19l-7-7 7-7']} />;

// ── Menu data ─────────────────────────────────────────────────────────────────
const mainMenuItems = [
  { id: 'dashboard',           Icon: IcoHome,     label: 'Dashboard' },
  { id: 'SchoolLocationSetup', Icon: IcoBuilding, label: 'Schools' },
  { id: 'database-setup',      Icon: IcoDatabase, label: 'DB Backup & Restore' },
  { id: 'SessionManagement',   Icon: IcoBuilding, label: 'Session Management' },
  { id: 'AppSetup',            Icon: IcoAndroid,  label: 'App Setup' },
  { id: 'Settings',            Icon: IcoCog,      label: 'Settings' },
];

const schoolSetupItems = [
  { id: 'initial-setup',           Icon: IcoCog,       label: 'Initial Setup' },
  { id: 'SetupClassesAndSections', Icon: IcoTable,     label: 'Classes & Sections' },
  { id: 'SubjectsSetup',           Icon: IcoBook,      label: 'Subjects' },
  { id: 'SetupTimetable',          Icon: IcoCalTime,   label: 'Timetable slots' },
  { id: 'Students-Csv',            Icon: IcoIdCard,    label: 'Students Csv' },
  { id: 'Teachers-Csv',            Icon: IcoUserPlus,  label: 'Teachers Csv' },
  { id: 'ExamsTypeSetup',          Icon: IcoClipboard, label: 'Exams Setup' },
  { id: 'SchoolLetterHeader',      Icon: IcoClipboard, label: 'Letter Header Setup' },
  // { id: 'ProfilePictureOptimizer', Icon: IcoSync,      label: 'PP Optimizer' },
  { id: 'FeeSetup',                Icon: IcoRupee,     label: 'Fee Setup' },
];

const SB_BG       = 'linear-gradient(180deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)';
const COLLAPSED_W = 72;
const HIDE_DELAY  = 180; // ms — gives time to move mouse to panel

// ── Styles ────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  .sb-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  @keyframes sb-fadeIn { from { opacity:0; transform:translateX(-4px); } to { opacity:1; transform:translateX(0); } }
  @keyframes sb-popIn  { from { opacity:0; transform:translateX(-8px) scale(0.96); } to { opacity:1; transform:translateX(0) scale(1); } }

  .sb-btn {
    width: 100%; padding: 11px 13px; margin-bottom: 4px;
    background: transparent; border: 1px solid transparent; border-radius: 12px;
    color: white; cursor: pointer; text-align: left;
    font-size: 0.91rem; font-weight: 400;
    transition: background 0.18s, border-color 0.18s;
    display: flex; align-items: center; gap: 11px;
    white-space: nowrap; overflow: hidden;
    font-family: 'DM Sans', sans-serif;
  }
  .sb-btn:hover  { background: rgba(255,255,255,0.10); }
  .sb-btn.active { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.25); font-weight: 600; }

  .sb-sub-btn {
    width: 100%; padding: 9px 12px; margin-bottom: 2px;
    background: transparent; border: none; border-radius: 9px;
    color: rgba(255,255,255,0.82); cursor: pointer; text-align: left;
    font-size: 0.87rem; font-weight: 400;
    transition: background 0.18s, color 0.18s;
    display: flex; align-items: center; gap: 10px;
    white-space: nowrap; overflow: hidden;
    font-family: 'DM Sans', sans-serif;
  }
  .sb-sub-btn:hover  { background: rgba(255,255,255,0.10); color: white; }
  .sb-sub-btn.active { background: rgba(255,255,255,0.16); color: white; font-weight: 600; }

  /* Float panel items */
  .sb-float-item {
    width: 100%; padding: 9px 12px; margin-bottom: 2px;
    background: transparent; border: none; border-radius: 9px;
    color: rgba(255,255,255,0.88); cursor: pointer; text-align: left;
    font-size: 0.87rem; font-weight: 400;
    transition: background 0.15s, color 0.15s;
    display: flex; align-items: center; gap: 10px;
    white-space: nowrap;
    font-family: 'DM Sans', sans-serif;
  }
  .sb-float-item:hover  { background: rgba(255,255,255,0.14); color: white; }
  .sb-float-item.active { background: rgba(255,255,255,0.22); color: white; font-weight: 600; }

  .sb-label { animation: sb-fadeIn 0.2s ease both; }

  .sb-toggle {
    width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
    background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.22);
    color: white; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.18s;
  }
  .sb-toggle:hover { background: rgba(255,255,255,0.26); }

  /* Float panel */
  .sb-float-panel {
    position: fixed;
    left: ${COLLAPSED_W + 10}px;
    z-index: 9999;
    min-width: 220px;
    background: linear-gradient(160deg, #1e3a8a 0%, #2855ae 100%);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 16px;
    padding: 8px;
    box-shadow: 0 16px 48px rgba(0,0,0,0.35), 0 2px 10px rgba(0,0,0,0.2);
    animation: sb-popIn 0.18s ease both;
  }
  .sb-float-title {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.7px; color: rgba(255,255,255,0.4);
    padding: 4px 10px 8px;
    border-bottom: 1px solid rgba(255,255,255,0.12);
    margin-bottom: 5px;
  }

  /* Tooltip chip for main items */
  /* Hide scrollbars while keeping scroll functionality */
  .sb-root nav::-webkit-scrollbar,
  .sb-root nav div::-webkit-scrollbar { display: none; }

  .sb-tip {
    position: fixed;
    left: ${COLLAPSED_W + 10}px;
    z-index: 9999;
    background: #0f172a;
    color: white;
    font-size: 12px; font-weight: 600;
    padding: 5px 12px; border-radius: 8px;
    white-space: nowrap;
    box-shadow: 0 4px 16px rgba(0,0,0,0.3);
    pointer-events: none;
    animation: sb-popIn 0.15s ease both;
  }
`;

// ── Hook: hover with delay-on-hide ───────────────────────────────────────────
// Returns [isOpen, triggerProps, panelProps]
// Mouse entering either the trigger or the panel keeps it open.
// Mouse leaving both (with a small delay) closes it.
function useHoverPanel(hideDelay = HIDE_DELAY) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enter = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setOpen(true);
  }, []);

  const leave = useCallback(() => {
    timer.current = setTimeout(() => setOpen(false), hideDelay);
  }, [hideDelay]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { open, triggerProps: { onMouseEnter: enter, onMouseLeave: leave }, panelProps: { onMouseEnter: enter, onMouseLeave: leave } };
}

// ── Component ─────────────────────────────────────────────────────────────────
const Sidebar: React.FC<SidebarProps> = ({ activeComponent, setActiveComponent, onLogout }) => {
  const [expanded, setExpanded]             = useState(true);
  const [schoolExpanded, setSchoolExpanded] = useState(true);
  const [submenuHeight, setSubmenuHeight]   = useState<number | null>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  // Per-button refs for measuring Y position of float panels/tips
  const schoolBtnRef = useRef<HTMLDivElement>(null);
  const mainBtnRefs  = useRef<Record<string, HTMLDivElement | null>>({});
  const [schoolTop, setSchoolTop] = useState(0);
  const [mainTops, setMainTops]   = useState<Record<string, number>>({});

  const isSchoolActive = schoolSetupItems.some(i => i.id === activeComponent);

  // Hover-panel state for school setup float
  const schoolHover = useHoverPanel();
  // Per-item hover state for main item tooltips
  const [hoveredMain, setHoveredMain] = useState<string | null>(null);
  const mainHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (submenuRef.current) setSubmenuHeight(submenuRef.current.scrollHeight);
  }, []);

  const updatePositions = useCallback(() => {
    if (schoolBtnRef.current)
      setSchoolTop(schoolBtnRef.current.getBoundingClientRect().top);
    const tops: Record<string, number> = {};
    Object.entries(mainBtnRefs.current).forEach(([id, el]) => {
      if (el) tops[id] = el.getBoundingClientRect().top;
    });
    setMainTops(tops);
  }, []);

  useEffect(() => {
    updatePositions();
    window.addEventListener('resize', updatePositions);
    return () => window.removeEventListener('resize', updatePositions);
  }, [expanded, updatePositions]);

  const W = expanded ? 260 : COLLAPSED_W;

  // Main item tooltip handlers (simple, no delay needed — pointer-events: none on tip)
  const mainEnter = (id: string) => {
    if (mainHoverTimer.current) clearTimeout(mainHoverTimer.current);
    setHoveredMain(id);
  };
  const mainLeave = () => {
    mainHoverTimer.current = setTimeout(() => setHoveredMain(null), 80);
  };

  return (
    <div
      className="sb-root"
      style={{
        width: W, minWidth: W,
        background: SB_BG,
        padding: expanded ? '20px 10px' : '20px 8px',
        color: 'white',
        minHeight: '100vh',
        boxShadow: '2px 0 20px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1), min-width 0.28s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'visible',
        position: 'relative',
        zIndex: 100,
      }}
    >
      <style>{CSS}</style>

      {/* ── Logo row ── */}
      {expanded ? (
        <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden', minWidth: 0 }}>
            <div style={{ width: 36, height: 36, flexShrink: 0, background: 'rgba(255,255,255,0.18)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IcoGrid size={17} style={{ color: 'white' }} />
            </div>
            <h2 className="sb-label" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
              SkoolPillar New
            </h2>
          </div>
          <button className="sb-toggle" onClick={() => setExpanded(p => !p)} title="Collapse sidebar">
            <IcoSidebarClose size={15} style={{ color: 'white' }} />
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.18)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IcoGrid size={17} style={{ color: 'white' }} />
          </div>
          <button
            className="sb-toggle"
            onClick={() => setExpanded(p => !p)}
            title="Expand sidebar"
            style={{ width: 36, height: 36, borderRadius: 10 }}
          >
            <IcoSidebarOpen size={15} style={{ color: 'white' }} />
          </button>
          <div style={{ width: 32, height: 1, background: 'rgba(255,255,255,0.15)', margin: '4px 0 2px' }} />
        </div>
      )}

      {/* ── Nav ── */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'visible', scrollbarWidth: 'none' }} onScroll={updatePositions}>

        {/* ── Main items ── */}
        {mainMenuItems.map(({ id, Icon, label }) =>
          expanded ? (
            <button
              key={id}
              className={`sb-btn ${activeComponent === id ? 'active' : ''}`}
              onClick={() => setActiveComponent(id)}
            >
              <Icon size={17} style={{ color: 'white', flexShrink: 0 }} />
              <span className="sb-label">{label}</span>
            </button>
          ) : (
            /* Collapsed: icon + tooltip chip */
            <div
              key={id}
              ref={el => { mainBtnRefs.current[id] = el; }}
              onMouseEnter={() => { updatePositions(); mainEnter(id); }}
              onMouseLeave={mainLeave}
            >
              <button
                className={`sb-btn ${activeComponent === id ? 'active' : ''}`}
                onClick={() => setActiveComponent(id)}
                style={{ justifyContent: 'center', padding: '11px' }}
              >
                <Icon size={17} style={{ color: 'white', flexShrink: 0 }} />
              </button>
              {/* Tooltip — pointer-events: none so it never blocks the trigger */}
              {hoveredMain === id && (
                <div className="sb-tip" style={{ top: (mainTops[id] ?? 0) + 9 }}>
                  {label}
                </div>
              )}
            </div>
          )
        )}

        {/* ── School Setup ── */}
        {expanded ? (
          <div style={{ marginBottom: 4 }}>
            <button
              className={`sb-btn ${isSchoolActive ? 'active' : ''}`}
              onClick={() => setSchoolExpanded(p => !p)}
              style={{ justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <IcoWrench size={17} style={{ color: 'white', flexShrink: 0 }} />
                <span className="sb-label">School Setup</span>
              </div>
              <IcoChevronDown
                size={14}
                style={{
                  color: 'rgba(255,255,255,0.6)', flexShrink: 0,
                  transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
                  transform: schoolExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              />
            </button>

            <div
              ref={submenuRef}
              style={{
                marginTop: 2, marginLeft: 10,
                borderLeft: '2px solid rgba(255,255,255,0.16)', paddingLeft: 4,
                maxHeight: schoolExpanded ? 'calc(100vh - 280px)' : '0px',
                opacity: schoolExpanded ? 1 : 0,
                overflowY: schoolExpanded ? 'auto' : 'hidden',
                overflowX: 'hidden',
                scrollbarWidth: 'none',
                transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease',
              }}
            >
              {schoolSetupItems.map(({ id, Icon, label }) => (
                <button
                  key={id}
                  className={`sb-sub-btn ${activeComponent === id ? 'active' : ''}`}
                  onClick={() => setActiveComponent(id)}
                >
                  <Icon size={15} style={{ flexShrink: 0, color: 'white' }} />
                  <span className="sb-label">{label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* ── Collapsed School Setup: icon + floating panel ── */
          <div style={{ marginBottom: 4 }}>
            {/* Trigger zone: the icon button */}
            <div
              ref={schoolBtnRef}
              {...schoolHover.triggerProps}
              onMouseEnter={() => { updatePositions(); schoolHover.triggerProps.onMouseEnter(); }}
            >
              <button
                className={`sb-btn ${isSchoolActive ? 'active' : ''}`}
                style={{ justifyContent: 'center', padding: '11px' }}
                onClick={() => { setExpanded(true); setSchoolExpanded(true); }}
              >
                <IcoWrench size={17} style={{ color: 'white', flexShrink: 0 }} />
              </button>
            </div>

            {/* Float panel — rendered in DOM when open, kept alive by panelProps */}
            {schoolHover.open && (
              <div
                className="sb-float-panel"
                style={{ top: schoolTop }}
                {...schoolHover.panelProps}
              >
                <div className="sb-float-title">School Setup</div>
                {schoolSetupItems.map(({ id, Icon, label }) => (
                  <button
                    key={id}
                    className={`sb-float-item ${activeComponent === id ? 'active' : ''}`}
                    onClick={() => setActiveComponent(id)}
                  >
                    <Icon size={15} style={{ flexShrink: 0, color: 'white' }} />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* ── Logout ── */}
      <div style={{ paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        {expanded ? (
          <button className="sb-btn" onClick={onLogout} style={{ marginBottom: 0 }}>
            <IcoSignOut size={17} style={{ color: 'white', flexShrink: 0 }} />
            <span className="sb-label">Logout</span>
          </button>
        ) : (
          <div
            onMouseEnter={() => { updatePositions(); mainEnter('__logout'); }}
            onMouseLeave={mainLeave}
          >
            <button className="sb-btn" onClick={onLogout} style={{ justifyContent: 'center', padding: '11px', marginBottom: 0 }}>
              <IcoSignOut size={17} style={{ color: 'white', flexShrink: 0 }} />
            </button>
            {hoveredMain === '__logout' && (
              <div className="sb-tip" style={{ top: (mainTops['__logout'] ?? 0) + 9 }}>
                Logout
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;